import { useSetState } from 'ahooks'

import { EDuration, EProductType } from '../type'
import { getProductItems } from '../helpers/getProductItems'
import { getPaidProductType, getProductType } from '../helpers/getProductType'

import { type IGetPaymentLinkProps, useGetPaymentLink } from './getPaymentLink'
import { useListPaymentPlatform } from './useListPaymentPlatform'
import { useGetPayInsiderToken } from './getPayInsiderToken'

import type { EPaidProductType } from '../type'
import type { TUseGetPayInsiderToken } from './getPayInsiderToken'
import type { $Enums } from '@prisma/client'
import type { TUseSubLog } from '@/contexts/SiteSpecificContext/hooks/subLog'

import { PaymentPlatformPlatformName } from '@/contexts/SiteSpecificContext/types'
import { useAnalyticsData } from '@/hooks/analyticsData'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'


import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
export interface ICheckoutInfo {
  clientSecret?: string | null
  trailClientSecret?: string | null
  pricesIdKey?: string
}

const defaultCheckoutInfo = {
  clientSecret: '',
  trailClientSecret: '',
  pricesIdKey: '',
}

export enum EPricingStep {
  BUY,
  PAY,
}

export interface IProductCheckoutMap {
  [EProductType.BASIC]: {
    [EDuration.MONTHLY]: ICheckoutInfo
    [EDuration.YEARLY]: ICheckoutInfo
  }
  [EProductType.PRO]: {
    [EDuration.MONTHLY]: ICheckoutInfo
    [EDuration.YEARLY]: ICheckoutInfo
  }
  [EProductType.UNLIMITED]: {
    [EDuration.MONTHLY]: ICheckoutInfo
    [EDuration.YEARLY]: ICheckoutInfo
  }
  [EProductType.ULTIMATE]: {
    [EDuration.MONTHLY]: ICheckoutInfo
    [EDuration.YEARLY]: ICheckoutInfo
  }
}

const defaultProductCheckoutMap = {
  [EProductType.BASIC]: {
    [EDuration.MONTHLY]: defaultCheckoutInfo,
    [EDuration.YEARLY]: defaultCheckoutInfo,
  },
  [EProductType.PRO]: {
    [EDuration.MONTHLY]: defaultCheckoutInfo,
    [EDuration.YEARLY]: defaultCheckoutInfo,
  },
  [EProductType.UNLIMITED]: {
    [EDuration.MONTHLY]: defaultCheckoutInfo,
    [EDuration.YEARLY]: defaultCheckoutInfo,
  },
  [EProductType.ULTIMATE]: {
    [EDuration.MONTHLY]: defaultCheckoutInfo,
    [EDuration.YEARLY]: defaultCheckoutInfo,
  },
}

export function getDefaultProductCheckoutMap() {
  return JSON.parse(JSON.stringify(defaultProductCheckoutMap))
}

function getPricesIdKey(pricesId: string[] = []) {
  return JSON.stringify(pricesId)
}

export interface IUsePayStep {
  plan: TUseSubLog
  onChangeStep?: (step: EPricingStep) => void
  appName: $Enums.SubLogAppName
  isModal: boolean
}

export function usePayStep({
  plan,
  onChangeStep,
  appName,
  isModal,
}: IUsePayStep) {
  const { isSub, expirePayment, planPlatformInfoEnable, planPlatformName } =
    plan
  const { isSignIn, userId, sessionAuthenticated } = useSessionInfo()

  const payDivContentRef = useRef<HTMLDivElement>(null)
  const buyDivContentRef = useRef<HTMLDivElement>(null)

  // 0: 购买流程、1: 付费流程
  const [step, setStep] = useState<EPricingStep>(EPricingStep.BUY)

  const [curPaidProduct, setCurPaidProduct] = useState<IGetPaymentLinkProps>()

  const { trackCustomEvent } = useAnalyticsData()

  const paymentPlatformInfo = useListPaymentPlatform({ plan })
  const {
    firstPaymentPlatformIsPaypal,
    firstPaymentPlatformIsFastspring,
    firstPaymentPlatformIsPayinsider,
  } = paymentPlatformInfo
  const hasReusableWebPaymentMethod =
    planPlatformInfoEnable && !!planPlatformName
  /**
   * Paypal 支付只有第一步
   */
  useEffect(() => {
    if (isSignIn && firstPaymentPlatformIsPaypal && step === EPricingStep.PAY) {
      setStep(EPricingStep.BUY)
    }
  }, [firstPaymentPlatformIsPaypal, isSignIn, step])

  /**
   * 如果已登录、且是付费用户，并且我们没有更换stripe账号，当前在付费流程则返回第一步
   */
  useEffect(() => {
    if (
      isSignIn &&
      isSub !== '' &&
      isSub &&
      step === EPricingStep.PAY &&
      !expirePayment &&
      hasReusableWebPaymentMethod
    ) {
      setStep(EPricingStep.BUY)
    }
  }, [isSignIn, step, isSub, expirePayment, hasReusableWebPaymentMethod])

  /* 触发结账上报 */
  useEffect(() => {
    if (sessionAuthenticated && isSub !== '' && !isSub && curPaidProduct) {
      const { productPriceList, appName, action, platformName } = curPaidProduct

      const items = getProductItems({
        productPriceList,
        appName,
      })
      const ecommerce = {
        currency: productPriceList[0].currency,
        appName,
        payment_platform: platformName,
        value: productPriceList.reduce((acc, cur) => {
          const currencyPrice = (cur.price * 1000) / 100 / 1000
          return (acc * 1000 + currencyPrice * 1000) / 1000
        }, 0),
        items,
        num_items: items.length,
        content_ids: productPriceList.map((p) => p.priceId),
      }
      if (action === 'create') {
        trackCustomEvent({ name: 'begin_checkout', meta: ecommerce })
      }
    }
  }, [curPaidProduct, isSub, sessionAuthenticated, trackCustomEvent])

  const onChangeBuyStep = () => {
    setStep(EPricingStep.BUY)
  }

  useEffect(() => {
    onChangeStep?.(step)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  // 存所有clientSecret
  const [productCheckoutMap, setProductCheckoutMap] =
    useSetState<IProductCheckoutMap>(getDefaultProductCheckoutMap())

  const updateCheckoutInfo = useCallback(
    ({
      productType,
      duration,
      clientSecret,
      trailClientSecret = '',
      pricesIdKey,
    }: {
      productType: EPaidProductType
      duration: EDuration
      clientSecret?: string | null
      trailClientSecret?: string | null
      pricesIdKey: string
    }) => {
      setProductCheckoutMap((prev) => ({
        ...prev,
        [productType]: {
          ...prev[productType],
          [duration]: {
            ...prev[productType][duration],
            clientSecret,
            trailClientSecret,
            pricesIdKey,
          },
        },
      }))
    },
    [setProductCheckoutMap],
  )

  const { getPaymentLink, isLoading: getPaymentLinkLoading } =
    useGetPaymentLink({
      appName,
    })

  const { getPayInsiderToken, getPayInsiderLoading } = useGetPayInsiderToken()

  const [payInsiderPayInfo, setPayInsiderPayInfo] =
    useState<TUseGetPayInsiderToken['payInsiderPayInfo']>(undefined)

  const onChangePayStep = async ({
    paidProduct,
  }: {
    paidProduct: IGetPaymentLinkProps
  }) => {
    const changeStep = () => {
      setCurPaidProduct(paidProduct)
      setStep(EPricingStep.PAY)

      setTimeout(() => {
        payDivContentRef.current?.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
          inline: 'center',
        })
      }, 100)
    }

    const product = paidProduct.productPriceList[0]
    const productType = getPaidProductType({ product })
    const duration = product.duration as EDuration
    const pricesIdKey = getPricesIdKey(paidProduct.pricesId)

    // Payinsider 处理
    if (firstPaymentPlatformIsPayinsider) {
      if (isSignIn) {
        const res = await getPayInsiderToken({
          priceId: paidProduct.pricesId[0],
        })
        setPayInsiderPayInfo(res)
        updateCheckoutInfo({
          productType,
          duration,
          clientSecret: res?.accessToken,
          trailClientSecret: '',
          pricesIdKey,
        })
      }

      changeStep()
      return
    }
    changeStep()
    if (sessionAuthenticated) {
      const res = await getPaymentLink({
        pricesId: paidProduct.pricesId,
        cancelUrl: window.location.href,
        platformName: paidProduct.platformName,
        appName,
        userId,
        productPriceList: paidProduct.productPriceList,
        action: paidProduct.action,
      })
      updateCheckoutInfo({
        productType,
        duration,
        clientSecret: res?.clientSecret,
        trailClientSecret: res?.trailClientSecret,
        pricesIdKey,
      })
    }
  }

  const curClientSecret = useMemo(() => {
    if (!curPaidProduct) {
      return ''
    }
    const product = curPaidProduct.productPriceList[0]
    const productType = getProductType({ product })

    const duration = product.duration as EDuration
    const pricesIdKey = getPricesIdKey(curPaidProduct.pricesId)
    if (productType !== EProductType.FREE) {
      const checkoutInfo =
        productCheckoutMap[productType as EPaidProductType][duration]
      if (checkoutInfo.pricesIdKey !== pricesIdKey) {
        return ''
      }

      return checkoutInfo.clientSecret
    }
    return ''
  }, [productCheckoutMap, curPaidProduct])

  // useEffect(() => {
  //   console.log(curPaidProduct, 'curPaidProduct')
  //   console.log(curClientSecret, 'curClientSecret')
  //   console.log(productCheckoutMap, 'productCheckoutMap')
  // }, [curClientSecret, curPaidProduct, productCheckoutMap])

  const [paymentLoading, setPaymentLoading] = useState(false)

  const onPaypalClick = async ({
    paidProduct,
  }: {
    paidProduct: IGetPaymentLinkProps
  }) => {
    const product = paidProduct.productPriceList[0]
    const productType = getPaidProductType({ product })

    const duration = product.duration as EDuration
    const pricesIdKey = getPricesIdKey(paidProduct.pricesId)
    const checkoutInfo = productCheckoutMap[productType][duration]
    const clientSecret =
      checkoutInfo.pricesIdKey === pricesIdKey ? checkoutInfo.clientSecret : ''
    // console.log(clientSecret, 'clientSecret')
    if (clientSecret && sessionAuthenticated) {
      window.location.href = clientSecret
    } else if (sessionAuthenticated && !clientSecret) {
      setPaymentLoading(true)
      const res = await getPaymentLink({
        pricesId: paidProduct.pricesId,
        cancelUrl: window.location.href,
        platformName: PaymentPlatformPlatformName.Paypal,
        appName,
        userId,
        productPriceList: paidProduct.productPriceList,
        action: paidProduct.action,
      })
      setPaymentLoading(false)
      if (res?.clientSecret) {
        window.location.href = res.clientSecret
      }
      updateCheckoutInfo({
        productType,
        duration,
        clientSecret: res?.clientSecret,
        trailClientSecret: res?.trailClientSecret,
        pricesIdKey,
      })
    }
    setCurPaidProduct(paidProduct)
  }

  return {
    step,
    onChangeBuyStep,
    onChangePayStep,
    curClientSecret,
    curPaidProduct,
    getPaymentLink,
    getPaymentLinkLoading,
    setProductCheckoutMap,
    isModal,
    appName,
    paymentPlatformInfo,
    onPaypalClick,
    paymentLoading,
    payDivContentRef,
    buyDivContentRef,
    getPayInsiderLoading,
    payInsiderPayInfo,
    getPayInsiderToken,
  }
}

export type TUsePayStep = ReturnType<typeof usePayStep>
